import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { UserPlus, Search, Loader2, CheckCircle2, XCircle, Mail, Phone, MapPin, ChevronLeft, ChevronRight, ShieldAlert } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { OwnerSelect } from '@/components/merchant/OwnerSelect';
import { useAuth, canModuleTab } from '@/hooks/useAuth';

/**
 * Stakeholders → Signup Requests.
 *
 * Every self-registration — the website's Create Account form, the portal's own sign-up and Shopify
 * installs — arrives here as a PENDING account. A sales executive reviews it and either moves it to
 * a working merchant (Approve) or turns it down with a reason (Reject). Gated by the
 * 'Signup Requests' permission: `view` to see the queue, `edit` to approve or reject.
 */

interface Req {
  id: number; companyName: string; brandName?: string | null; ownerName?: string | null;
  email: string; mobile: string; emailVerified: boolean; mobileVerified: boolean;
  status: string; onboardingStatus?: string | null; onboardingSubmittedAt?: string | null;
  serviceType?: string | null; city?: string | null; state?: string | null; pincode?: string | null;
  gstin?: string | null; pan?: string | null; source: string; sourceLabel: string; notes?: string | null;
  salesUserName?: string | null; approvedByName?: string | null; createdAt: string; approvedAt?: string | null;
}
type Tab = 'pending' | 'approved' | 'rejected';

const ONBOARDING: Record<string, { label: string; cls: string }> = {
  NOT_STARTED: { label: 'Onboarding not started', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  IN_PROGRESS: { label: 'Onboarding in progress', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  SUBMITTED: { label: 'KYC submitted', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  COMPLETED: { label: 'Onboarding complete', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const fmt = (d?: string | null) => d
  ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';
const errMsg = (e: any, fallback: string) => {
  const m = e?.response?.data?.message;
  return Array.isArray(m) ? m.join(' ') : (m || fallback);
};

export function SignupRequestsPage() {
  const user = useAuth((s) => s.user);
  const canView = canModuleTab(user, 'Signup Requests', 'All', 'view');
  const canAct = canModuleTab(user, 'Signup Requests', 'All', 'edit');
  const [tab, setTab] = useState<Tab>('pending');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [approving, setApproving] = useState<Req | null>(null);
  const [rejecting, setRejecting] = useState<Req | null>(null);
  const pageSize = 50;

  useEffect(() => { const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [tab]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['signup-requests', tab, q, page],
    enabled: canView,
    queryFn: async () => (await api.get<{ total: number; counts: { pending: number; rejected: number }; items: Req[] }>(
      '/merchants/signup-requests', { params: { tab, page, pageSize, ...(q ? { search: q } : {}) } },
    )).data,
  });

  if (!canView) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-600">
        <ShieldAlert className="mx-auto mb-2 text-slate-400" />
        You don't have permission to view Signup Requests. Ask an administrator to grant it under Users → Roles &amp; Permissions.
      </div>
    );
  }

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const tabs: Array<{ key: Tab; label: string; count?: number }> = [
    { key: 'pending', label: 'Pending', count: data?.counts.pending },
    { key: 'approved', label: 'Approved (90 days)' },
    { key: 'rejected', label: 'Rejected', count: data?.counts.rejected },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><UserPlus size={20} /> Signup Requests</h1>
        <p className="text-sm text-slate-500">Every registration from the website and the portal lands here. Check it, then approve it into a merchant or reject it.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${tab === t.key ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
            {t.label}{t.count != null ? <span className="ml-1.5 opacity-80">{t.count}</span> : null}
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search business, name, email, mobile, city"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>
      ) : isError ? (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">{errMsg(error, 'Could not load signup requests.')}</div>
      ) : !data?.items.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500 text-sm">
          {tab === 'pending' ? 'No registrations waiting for review.' : 'Nothing here.'}
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((r) => {
            const ob = ONBOARDING[r.onboardingStatus ?? ''] ?? null;
            return (
              <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-900">{r.companyName}</span>
                  {r.brandName && <span className="text-sm text-slate-500">({r.brandName})</span>}
                  <span className="text-xs text-slate-400">#{r.id}</span>
                  {tab === 'pending' && ob && <span className={`text-xs px-2 py-0.5 rounded-full border ${ob.cls}`}>{ob.label}</span>}
                  <span className="text-xs px-2 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200">{r.sourceLabel}</span>
                  <div className="ml-auto flex items-center gap-2">
                    {tab === 'pending' && canAct && (
                      <>
                        <button onClick={() => setApproving(r)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg px-3 py-1.5 hover:bg-emerald-700">
                          <CheckCircle2 size={14} /> Approve
                        </button>
                        <button onClick={() => setRejecting(r)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-700 border border-rose-200 rounded-lg px-3 py-1.5 hover:bg-rose-50">
                          <XCircle size={14} /> Reject
                        </button>
                      </>
                    )}
                    {tab === 'approved' && (
                      <Link to={`/merchants/${r.id}`} className="text-sm font-medium text-primary hover:underline">Open merchant</Link>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1.5 mt-3 text-sm text-slate-700">
                  <div className="truncate">{r.ownerName || '—'}</div>
                  <div className="flex items-center gap-1.5 min-w-0"><Mail size={14} className="text-slate-400 shrink-0" />
                    <span className="truncate">{r.email}</span>{r.emailVerified && <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />}</div>
                  <div className="flex items-center gap-1.5"><Phone size={14} className="text-slate-400" /> {r.mobile}
                    {r.mobileVerified && <CheckCircle2 size={13} className="text-emerald-600" />}</div>
                  <div className="flex items-center gap-1.5 min-w-0"><MapPin size={14} className="text-slate-400 shrink-0" />
                    <span className="truncate">{[r.city, r.state, r.pincode].filter(Boolean).join(', ') || '—'}</span></div>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                  <span>Registered {fmt(r.createdAt)}</span>
                  {r.serviceType && <span>Service: {r.serviceType}</span>}
                  {r.gstin && <span>GSTIN {r.gstin}</span>}
                  {r.pan && <span>PAN {r.pan}</span>}
                  {tab === 'approved' && <span>Approved {fmt(r.approvedAt)}{r.approvedByName ? ` by ${r.approvedByName}` : ''}</span>}
                  {tab === 'approved' && r.salesUserName && <span>Sales owner: {r.salesUserName}</span>}
                </div>
                {tab === 'rejected' && r.notes && <div className="mt-2 text-xs text-rose-700">Reason: {r.notes}</div>}
              </div>
            );
          })}
        </div>
      )}

      {total > pageSize && (
        <div className="flex items-center justify-end gap-2 text-sm text-slate-600">
          <span>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40"><ChevronLeft size={16} /></button>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40"><ChevronRight size={16} /></button>
        </div>
      )}

      {approving && <ApproveModal req={approving} myId={user?.kind === 'user' ? user.id : undefined} onClose={() => setApproving(null)} />}
      {rejecting && <RejectModal req={rejecting} onClose={() => setRejecting(null)} />}
    </div>
  );
}

function ApproveModal({ req, myId, onClose }: { req: Req; myId?: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [salesUserId, setSalesUserId] = useState<number | null>(myId ?? null);
  const [billingMode, setBillingMode] = useState<'PREPAID' | 'POSTPAID'>('PREPAID');
  const [notes, setNotes] = useState('');
  const m = useMutation({
    mutationFn: async () => (await api.post(`/merchants/signup-requests/${req.id}/approve`, { salesUserId: salesUserId ?? undefined, billingMode, notes: notes.trim() || undefined })).data,
    onSuccess: () => {
      toast.success(`${req.companyName} is now a merchant.`);
      qc.invalidateQueries({ queryKey: ['signup-requests'] });
      qc.invalidateQueries({ queryKey: ['merchants'] });
      onClose();
    },
    onError: (e: any) => toast.error(errMsg(e, 'Could not approve.')),
  });
  return (
    <Modal isOpen onClose={onClose} title="Approve registration" subtitle={`${req.companyName} becomes a working merchant`} maxWidth="md">
      <div className="space-y-3 text-sm">
        <label className="block">
          <span className="text-slate-600">Sales owner</span>
          <div className="mt-1"><OwnerSelect value={salesUserId} onChange={setSalesUserId} preferType="SALES_PERSON" placeholder="Choose the sales owner" /></div>
          <span className="text-xs text-slate-400">Left empty, it is assigned to you.</span>
        </label>
        <label className="block">
          <span className="text-slate-600">Billing</span>
          <select value={billingMode} onChange={(e) => setBillingMode(e.target.value as any)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg bg-white">
            <option value="PREPAID">Prepaid (wallet)</option>
            <option value="POSTPAID">Postpaid</option>
          </select>
        </label>
        <label className="block">
          <span className="text-slate-600">Notes (optional)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg" />
        </label>
        <p className="text-xs text-slate-500">They still need KYC, billing, a pickup location and a courier before they can ship — the portal asks them for these.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200">Cancel</button>
          <button onClick={() => m.mutate()} disabled={m.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium disabled:opacity-50">
            {m.isPending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Approve
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RejectModal({ req, onClose }: { req: Req; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: async () => (await api.post(`/merchants/signup-requests/${req.id}/reject`, { reason: reason.trim() })).data,
    onSuccess: () => {
      toast.success(`${req.companyName} rejected.`);
      qc.invalidateQueries({ queryKey: ['signup-requests'] });
      qc.invalidateQueries({ queryKey: ['merchants'] });
      onClose();
    },
    onError: (e: any) => toast.error(errMsg(e, 'Could not reject.')),
  });
  return (
    <Modal isOpen onClose={onClose} title="Reject registration" subtitle={req.companyName} maxWidth="md">
      <div className="space-y-3 text-sm">
        <label className="block">
          <span className="text-slate-600">Reason *</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={2000} autoFocus
            placeholder="e.g. duplicate of an existing account, could not verify the business"
            className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg" />
        </label>
        <p className="text-xs text-slate-500">A rejected account cannot sign in.</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200">Cancel</button>
          <button onClick={() => m.mutate()} disabled={!reason.trim() || m.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 text-white font-medium disabled:opacity-50">
            {m.isPending ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />} Reject
          </button>
        </div>
      </div>
    </Modal>
  );
}
